import { Component, Input, inject } from '@angular/core';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * ProductDetailsStoricoComponent — pannello "Prodotti" embedded dentro
 * `<app-storico-ordini-dettagli>` (Chunk H.2). Mostra l'elenco dei
 * prodotti di un ordine chiuso, con sub-products espandibili quando
 * presenti, e accesso ai sub-modali QR / dettagli prodotto.
 *
 * NB: NON è un modale autonomo (no backdrop, no overlay). È renderizzato
 * inline nel modale padre come accordion-section.
 *
 * Sub-modali aperti dalle action buttons:
 *   • app-product-detail-modal — dettagli prodotto (apre da ℹ️)
 *   • app-products-qrcodes — codici QR del prodotto (apre da 🔲)
 *   • app-subproduct-qrcode — codici QR sub-product
 *
 * Phase 1.5 / v2.9.0 — TS cleaned up (Chunk H.5):
 *   • dropped 6 console.log/debug (incluso "Barcode caca"),
 *   • dropped `clickedProduct` come state field (era solo passthrough
 *     all'input del sub-modal, ora locale alla call),
 *   • dropped `onAccordionChange` (gestito direttamente con
 *     `toggleAccordion()` su click native button),
 *   • `hasValidBarcodes` + `hasValidBarcodesSub` resi safe-guard
 *     contro array vuoto / null,
 *   • tipi `@Input order: any` → `IOrder` model,
 *   • switch a `inject()`.
 *
 * Behaviour preserved 1:1: openProd / seeQRON / seeQrSubOn flow,
 * accordion expand/collapse semantics.
 */
@Component({
  selector: 'app-product-details-storico',
  templateUrl: './product-details-storico.component.html',
  styleUrls: ['./product-details-storico.component.scss'],
  standalone: false,
})


export class ProductDetailsStoricoComponent {

  // ── Injected services ─────────────────────────────────────────────
  public readonly closeServ = inject(OpenModalService);

  // ── Inputs ────────────────────────────────────────────────────────
  /** Order BE shape — loose-typed by legacy convention (mixed
   *  Products.Product[] + Products.SubProducts[] nested). */
  @Input() order: any = null;
  @Input() opened = false;

  // ── UI state ──────────────────────────────────────────────────────
  /** Accordion currently expanded (`'accordion-' + i` o null). */
  public activeAccordion: string | null = null;
  /** Payload temporaneo per i sub-modali QR / dettagli. */
  public barcodes: any = null;
  public clickedProduct: string | null = null;

  // ──────────────────────────────────────────────────────────────────
  // Accordion (native client-side).
  // ──────────────────────────────────────────────────────────────────

  toggleAccordion(index: number): void {
    const key = `accordion-${index}`;
    this.activeAccordion = this.activeAccordion === key ? null : key;
  }

  isAccordionOpen(index: number): boolean {
    return this.activeAccordion === `accordion-${index}`;
  }

  // ──────────────────────────────────────────────────────────────────
  // Barcode validity helpers (safe against null/empty arrays).
  // ──────────────────────────────────────────────────────────────────

  hasValidBarcodes(prod: any): boolean {
    return !!prod?.barcodes?.some((b: any) => b?.barcode_text !== null);
  }

  hasValidBarcodesSub(sub: any): boolean {
    return !!sub?.barcodes?.some((b: any) => b?.barcode_text !== null);
  }

  // ──────────────────────────────────────────────────────────────────
  // Sub-modal openers (3 modali separati, preserved as-is).
  // ──────────────────────────────────────────────────────────────────

  /** Apre il modale QR per un prodotto top-level. */
  seeQrCodes(barcode: any): void {
    this.barcodes = barcode;
    this.closeServ.seeQRON();
  }

  /** Apre il modale QR per un sub-product. */
  seeQrCodesSub(barcode: any): void {
    this.barcodes = barcode;
    this.closeServ.seeQrSubOn();
  }

  /** Apre il modale di dettagli prodotto. */
  goToProductDetails(productCode: string): void {
    this.clickedProduct = productCode;
    this.closeServ.prodDetOn();
  }
  
}
